const OPTION_KEYS = ["A", "B", "C", "D"];

const asString = (value, fallback = "") => (
  typeof value === "string" ? value : fallback
);

const asUpperAnswer = (value) => {
  const answer = asString(value, "A").trim().toUpperCase();
  return OPTION_KEYS.includes(answer) ? answer : "A";
};

const emptyOptionMap = (factory) => OPTION_KEYS.reduce(
  (result, key) => ({ ...result, [key]: factory(key) }),
  {},
);

const mediaRef = (candidate, { kind, usageKey, optionKey = null } = {}) => {
  if (!candidate) return null;
  const source = typeof candidate === "string" ? { url: candidate } : candidate;
  const url = asString(
    source.playback_url
      || source.playbackUrl
      || source.external_url
      || source.externalUrl
      || source.stream_url
      || source.streamUrl
      || source.url,
  ).trim();
  if (!url) return null;
  return {
    url,
    kind: asString(source.media_type || source.mediaType || source.kind, kind || "unknown"),
    usageKey: asString(source.usage_key || source.usageKey, usageKey || ""),
    optionKey: asString(source.option_key || source.optionKey, optionKey || "") || null,
    assetId: asString(source.asset_id || source.assetId) || null,
    bindingId: asString(source.binding_id || source.bindingId) || null,
    sourceType: asString(source.source_type || source.sourceType, "existing"),
  };
};

const workspaceState = (environment, workspace = {}) => ({
  environment,
  saveState: asString(workspace.saveState || workspace.save_state, "saved"),
  syncState: asString(workspace.syncState || workspace.sync_state, environment === "center" ? "center" : "local_only"),
  permissions: workspace.permissions || {},
});

const baseModel = ({ source, question, content, taxonomy, media, workspace }) => ({
  schemaVersion: 1,
  source,
  id: asString(question?.id || question?._id),
  code: asString(question?.question_code || question?.code).trim().toUpperCase(),
  displayType: asString(question?.display_type || question?.displayType, "T"),
  status: asString(question?.status, "active"),
  content,
  taxonomy,
  media,
  workspace,
});

export { OPTION_KEYS, mediaRef };

export function adaptCenterQuestion(question = {}, options = {}) {
  const optionContent = emptyOptionMap((key) => asString(question[`option_${key.toLowerCase()}`]));
  const optionMedia = emptyOptionMap((key) => ({
    visual: mediaRef(question[`option_${key.toLowerCase()}_image_url`], {
      kind: "image",
      usageKey: "option.visual",
      optionKey: key,
    }),
  }));
  const feedback = options.optionFeedback || question.option_feedback || emptyOptionMap(() => []);

  return baseModel({
    source: "center",
    question,
    content: {
      questionHtml: asString(question.question_text),
      options: optionContent,
      correctAnswer: asUpperAnswer(question.correct_answer),
      explanationHtml: asString(question.solution_text || question.explanation),
    },
    taxonomy: {
      lesson: asString(question.lesson_name),
      topic: asString(question.topic),
      subtopic: asString(question.subtopic),
      unit: asString(question.unit_name),
      levels: [
        asString(question.taxonomy_level_1),
        asString(question.taxonomy_level_2),
        asString(question.taxonomy_level_3),
      ],
    },
    media: {
      question: {
        primaryVisual: mediaRef(question.question_image_url, { kind: "image", usageKey: "question.primary_visual" }),
        promptVideo: mediaRef(question.question_video_url, { kind: "video", usageKey: "question.prompt_video" }),
        gif: mediaRef(question.question_gif_url, { kind: "gif", usageKey: "question.gif" }),
        animation: mediaRef(question.animation_video_url, { kind: "animation", usageKey: "question.animation" }),
      },
      options: optionMedia,
      optionFeedback: feedback,
      explanation: {
        video: mediaRef(question.explanation_video_horizontal_url, { kind: "video", usageKey: "question.explanation_video" }),
        visual: mediaRef(question.explanation_image_url, { kind: "image", usageKey: "question.explanation_visual" }),
      },
      audio: {
        question: mediaRef(options.questionAudio, { kind: "audio", usageKey: "question.audio" }),
        solution: mediaRef(options.solutionAudio, { kind: "audio", usageKey: "question.solution_audio" }),
      },
      topic: {
        shortVideo: mediaRef(question.short_video_vertical_url, { kind: "video", usageKey: "topic.video_short" }),
      },
    },
    workspace: workspaceState("center", options.workspace),
  });
}

export function adaptHubQuestion(question = {}, options = {}) {
  const hubOptions = question.options || {};
  const optionImages = question.option_images || {};
  const questionMedia = question.media || {};
  const feedback = options.optionFeedback || question.option_feedback || emptyOptionMap(() => []);

  return baseModel({
    source: "hub",
    question,
    content: {
      questionHtml: asString(question.question_text),
      options: emptyOptionMap((key) => asString(hubOptions[key])),
      correctAnswer: asUpperAnswer(question.correct_answer),
      explanationHtml: asString(question.solution_text || question.explanation),
    },
    taxonomy: {
      lesson: asString(question.lesson || question.lesson_name),
      topic: asString(question.topic),
      subtopic: asString(question.subtopic),
      unit: asString(question.unit || question.unit_name),
      levels: [
        asString(question.taxonomy_level_1),
        asString(question.taxonomy_level_2),
        asString(question.taxonomy_level_3),
      ],
    },
    media: {
      question: {
        primaryVisual: mediaRef(questionMedia.image_url, { kind: "image", usageKey: "question.primary_visual" }),
        promptVideo: mediaRef(questionMedia.video_url, { kind: "video", usageKey: "question.prompt_video" }),
        gif: mediaRef(questionMedia.gif_url, { kind: "gif", usageKey: "question.gif" }),
        animation: mediaRef(questionMedia.animation_url, { kind: "animation", usageKey: "question.animation" }),
      },
      options: emptyOptionMap((key) => ({
        visual: mediaRef(optionImages[key], { kind: "image", usageKey: "option.visual", optionKey: key }),
      })),
      optionFeedback: feedback,
      explanation: {
        video: mediaRef(questionMedia.explanation_video_url, { kind: "video", usageKey: "question.explanation_video" }),
        visual: mediaRef(questionMedia.explanation_image_url, { kind: "image", usageKey: "question.explanation_visual" }),
      },
      audio: {
        question: mediaRef(questionMedia.audio_url, { kind: "audio", usageKey: "question.audio" }),
        solution: mediaRef(questionMedia.solution_audio_url, { kind: "audio", usageKey: "question.solution_audio" }),
      },
      topic: {
        shortVideo: mediaRef(questionMedia.short_video_url, { kind: "video", usageKey: "topic.video_short" }),
      },
    },
    workspace: workspaceState("hub", options.workspace || question.workspace),
  });
}

export function toCenterContentPatch(model) {
  return {
    question_code: model.code,
    display_type: model.displayType,
    status: model.status,
    question_text: model.content.questionHtml,
    option_a: model.content.options.A,
    option_b: model.content.options.B,
    option_c: model.content.options.C,
    option_d: model.content.options.D,
    correct_answer: model.content.correctAnswer,
    solution_text: model.content.explanationHtml,
    lesson_name: model.taxonomy.lesson,
    topic: model.taxonomy.topic,
    subtopic: model.taxonomy.subtopic,
    unit_name: model.taxonomy.unit,
    taxonomy_level_1: model.taxonomy.levels[0],
    taxonomy_level_2: model.taxonomy.levels[1],
    taxonomy_level_3: model.taxonomy.levels[2],
  };
}

export function toHubDraft(model) {
  return {
    question_text: model.content.questionHtml,
    options: { ...model.content.options },
    option_images: emptyOptionMap((key) => model.media.options[key]?.visual?.url || ""),
    option_feedback: emptyOptionMap((key) => {
      const value = model.media.optionFeedback[key];
      const list = Array.isArray(value) ? value : (value ? [value] : []);
      return list.map((item) => ({ url: item.url, kind: item.kind, usage_key: "option.feedback", option_key: key }));
    }),
    correct_answer: model.content.correctAnswer,
    solution_text: model.content.explanationHtml,
    display_type: model.displayType,
    media: {
      image_url: model.media.question.primaryVisual?.url || "",
      video_url: model.media.question.promptVideo?.url || "",
      gif_url: model.media.question.gif?.url || "",
      animation_url: model.media.question.animation?.url || "",
      explanation_video_url: model.media.explanation.video?.url || "",
      explanation_image_url: model.media.explanation.visual?.url || "",
      audio_url: model.media.audio.question?.url || "",
      solution_audio_url: model.media.audio.solution?.url || "",
      short_video_url: model.media.topic.shortVideo?.url || "",
    },
  };
}

export function getQuestionAnswerState(model, selectedAnswer = null) {
  const selected = asString(selectedAnswer).trim().toUpperCase();
  const answered = OPTION_KEYS.includes(selected);
  const correctAnswer = asUpperAnswer(model?.content?.correctAnswer);
  return {
    selectedAnswer: answered ? selected : null,
    correctAnswer,
    answered,
    isCorrect: answered && selected === correctAnswer,
  };
}
